import { type MatchSpec, matchName } from '../utils/match_name.js'

const authErrorCode = 401

export interface Ctx {
  headers?: Record<string, unknown>
  auth_info?: Record<string, unknown>
  [k: string]: unknown
}

type Fn = (ctx: Ctx, ...args: unknown[]) => unknown | Promise<unknown>

interface WorkerClient {
  callByName(name: string, method: string, params: unknown[]): Promise<unknown>
}

interface CachedToken {
  authInfo: Record<string, unknown>
  expiresAt: number
}

// Shared token cache (used by Auth and SimplePermissionControl via verifyBearerToken)
const tokenCache = new Map<string, CachedToken>()
const CACHE_TTL = 60_000 // 60 seconds

const cacheToken = (accessToken: string, authInfo: Record<string, unknown>): void => {
  tokenCache.set(accessToken, { authInfo, expiresAt: Date.now() + CACHE_TTL })
  if (tokenCache.size > 1000) {
    const now = Date.now()
    for (const [key, val] of tokenCache) {
      if (now >= val.expiresAt) tokenCache.delete(key)
    }
  }
}

/**
 * Verify a Bearer token from ctx headers. Returns authInfo or null.
 * Shared by Auth middleware and SimplePermissionControl.
 */
export const verifyBearerToken = async (
  ctx: Ctx,
  getWorkerClient: () => WorkerClient,
): Promise<Record<string, unknown> | null> => {
  const auth = ctx.headers?.authorization as string | undefined
  const accessToken = auth?.startsWith('Bearer ') ? auth.slice(7) : null
  if (!accessToken) return null

  const cached = tokenCache.get(accessToken)
  if (cached && Date.now() < cached.expiresAt) return cached.authInfo

  const client = getWorkerClient()
  const authInfo = (await Promise.race([
    client.callByName('user-auth-worker', 'verifyAccessToken', [{ accessToken }]),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('verifyAccessToken timeout after 10000ms')), 10000),
    ),
  ])) as Record<string, unknown> | null | undefined

  if (!authInfo?.sub) return null

  cacheToken(accessToken, authInfo)
  return authInfo
}

/**
 * Check if error is an authentication error (code 401)
 */
export const isAuthError = (error: unknown): boolean =>
  (error as { code?: number })?.code === authErrorCode

export interface AuthOptions {
  getWorkerClient: () => WorkerClient
  match: MatchSpec
}

export const Auth = ({ getWorkerClient, match }: AuthOptions) => {
  return (name: string, fn: Fn): Fn => {
    if (!matchName(match, name)) return fn

    return async (ctx, ...args) => {
      const auth_info = await verifyBearerToken(ctx, getWorkerClient)
      if (!auth_info) {
        const err = new Error('auth verify failed: no valid access token') as Error & {
          code?: number
        }
        err.code = authErrorCode
        throw err
      }
      ctx.auth_info = auth_info
      return fn(ctx, ...args)
    }
  }
}
